import React, { FC, useCallback, useEffect } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ILocation } from '@rbi-ctg/frontend';
import { GoogleGeolocationLibraryLoader } from 'components/google-geolocation-library-loader';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useMediaQuery from 'hooks/use-media-query';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useServiceModeContext } from 'state/service-mode';
import { isValidPosition } from 'utils/geolocation';
import { routes } from 'utils/routing';
import { ServiceModeCategory } from 'utils/service-mode';

import useSearchRestaurants from '../hooks/use-search-restaurants';
import useStoreLocatorView from '../hooks/use-store-locator-view';
import { DeliveryRoutes } from '../routes';

import { useServiceModeCategory } from './hooks/use-service-mode-category';
import { useStoresReducer } from './hooks/use-stores-reducer';
import StoreLocatorRoutes from './store-locator-routes';
import { StoreLocatorView } from './store-locator.view';

/**
 *
 * StoreLocator purpose
 *
 */

const StoreLocator: FC = () => {
  const { formatMessage } = useIntl();

  const isDesktop = useMediaQuery('desktop');

  const isDeliveryOnlyEnabled = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_ONLY);
  const orderingEnabled = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const { storeLocatorCallbackUrl, setStoreLocatorCallbackUrl } = useLocationContext();
  const redirectUrl = storeLocatorCallbackUrl || routes.menu;

  // cleaning up the callback url once the user gets away from the store locator page
  useEffectOnUnmount(() => {
    setStoreLocatorCallbackUrl('');
  });

  const {
    errorFavs,
    errorNearby,
    errorRecent,
    searchingNearby,
    searchingRecent,
    searchingFavs,
    storesNearby,
    storesFavs,
    storesRecent,
    storeLocatorDispatch,
  } = useStoresReducer();
  const { serviceMode, setServiceMode } = useServiceModeContext();

  const {
    activeCoordinates,
    manualAddress,
    isPermissionGranted,
    resetCoordsAndLoadCurrentPosition,
    setCoordinatesManual,
    setManualAddress,
    updateManualCoordinates,
  } = useGeolocation();

  const {
    ErrorDialog,
    Dialog,
    searchFavRestaurants,
    searchNearbyRestaurants,
    searchRecentRestaurants,
  } = useSearchRestaurants({
    storeLocatorDispatch,
  });

  const { currentView, updateStoreLocatorView } = useStoreLocatorView();

  const {
    serviceModeCategory,
    setServiceModeCategory,
    serviceModeCategoryOptions,
  } = useServiceModeCategory();

  const navigate = useNavigate();
  const { logEvent } = useMParticleContext();

  const onServiceModeCategoryChange = useCallback(
    (value: ServiceModeCategory) => {
      setServiceModeCategory(value);

      logEvent(CustomEventNames.BUTTON_CLICK, EventTypes.Other, {
        Name: CustomEventNames.BUTTON_CLICK_SERVICE_MODE_TOGGLE,
        'Service Mode': value,
      });

      switch (value) {
        case ServiceModeCategory.PICKUP:
          navigate(routes.storeLocator);
          setServiceMode(null);
          break;
        case ServiceModeCategory.DELIVERY:
          navigate(routes.address);
          break;
        case ServiceModeCategory.CATERING:
          navigate(routes.serviceModeCatering);
          break;
        default:
          break;
      }
    },
    [logEvent, navigate, setServiceMode, setServiceModeCategory]
  );

  const onSearchClick = useCallback(
    (location: ILocation) => {
      setCoordinatesManual({ newCoords: location });
      setManualAddress('');
    },
    [setCoordinatesManual, setManualAddress]
  );

  useEffect(() => {
    if (isValidPosition(activeCoordinates)) {
      searchNearbyRestaurants({
        location: activeCoordinates,
      });
    }
    // Triggers a search when the service mode change to keep the list in sync
  }, [activeCoordinates, searchNearbyRestaurants, serviceMode]);

  useEffect(() => {
    searchFavRestaurants(activeCoordinates);
  }, [activeCoordinates, searchFavRestaurants]);

  useEffect(() => {
    searchRecentRestaurants(activeCoordinates);
  }, [activeCoordinates, searchRecentRestaurants]);

  const onSearchBarValueChange = useCallback(
    (address: string | undefined | null) => {
      setManualAddress(address || '');
    },
    [setManualAddress]
  );

  const dismissAddressModalUrl = routes.storeLocator;

  if (isDeliveryOnlyEnabled) {
    return (
      <DeliveryRoutes
        deliveryModalPath="*"
        dismissAddressModalUrl={routes.storeLocator}
        redirectUrl={redirectUrl}
      />
    );
  }

  return (
    <>
      <Helmet title={formatMessage({ id: 'restaurants' })} />
      <StoreLocatorView
        activeCoordinates={activeCoordinates}
        errorFavs={errorFavs}
        errorNearby={errorNearby}
        errorRecent={errorRecent}
        handleResetLocation={resetCoordsAndLoadCurrentPosition}
        searchingNearby={searchingNearby}
        searchingRecent={searchingRecent}
        searchingFavs={searchingFavs}
        searchNearbyRestaurants={searchNearbyRestaurants}
        storesFavs={storesFavs}
        storesNearby={storesNearby}
        storesRecent={storesRecent}
        // Map View Props
        onSearchClick={onSearchClick}
        // View Props
        currentView={currentView}
        onViewChange={updateStoreLocatorView}
        // Search Bar Props
        onAddressSelected={updateManualCoordinates}
        searchBarValue={manualAddress}
        onSearchBarValueChange={onSearchBarValueChange}
        shouldFocusSearchBar={!manualAddress && isPermissionGranted && isDesktop}
        // Service Mode Category Props
        hideServiceModeCategories={!orderingEnabled}
        selectedServiceModeCategory={serviceModeCategory}
        serviceModeCategoryOptions={serviceModeCategoryOptions}
        onServiceModeCategoryChange={onServiceModeCategoryChange}
      />

      <StoreLocatorRoutes
        dismissAddressModalUrl={dismissAddressModalUrl}
        redirectUrl={redirectUrl}
      />

      <ErrorDialog />
      <Dialog />
    </>
  );
};

export default () => (
  <GoogleGeolocationLibraryLoader>
    <StoreLocator />
  </GoogleGeolocationLibraryLoader>
);
